import type { WorkbookColumnarSheet } from "@erp/shared-types";
import type { ColumnRead, ColumnReadContext } from "../columnReads";
import { formulaLookups, type TypedFormula } from "../formula";
import { lexFormula } from "../formulaLexer";
import type { Schema } from "../planTypes";
import { formulaTableIds } from "../tableReferences";

type FormulaLookup = Extract<TypedFormula, { kind: "lookup" }>;

/** Every table a columnar Worksheet reads, each read whole. */
function readsWhole(
  sheet: WorkbookColumnarSheet,
  context: ColumnReadContext,
): ColumnRead[] {
  const ids = new Set([
    sheet.inputId,
    ...sheet.lookups.map((lookup) => lookup.sourceId),
    ...sheet.columns.flatMap((column) =>
      formulaTableIds(column.expression, context.items),
    ),
  ]);
  return [...ids].map((tableId) => ({ tableId, columns: "all" }));
}

/**
 * The input Fields a columnar Worksheet names: in its Formulas (Column
 * references, a Formula lookup's value and if-not-found among them), its
 * Lookups' input columns, its filters and its sort.
 */
function inputColumns(sheet: WorkbookColumnarSheet, input: Schema): string[] {
  const fields = new Set(input.map(({ name }) => name));
  const named = [
    ...sheet.columns.flatMap((column) =>
      lexFormula(column.expression).flatMap((token) =>
        token.kind === "column" ? [token.name] : [],
      ),
    ),
    ...sheet.lookups.map((lookup) => lookup.inputColumn),
    ...sheet.filters.map((filter) => filter.column),
    ...sheet.sort.map((sort) => sort.column),
  ];
  return [...new Set(named.filter((name) => fields.has(name)))];
}

/**
 * The columns a Formula lookup reads of its table: its match and return
 * columns. VLOOKUP names its return column by position, so it reads every
 * column up to that one, keeping each column's position.
 */
function lookupColumns(
  lookup: FormulaLookup,
  table: Schema | undefined,
): string[] {
  const { matchColumn, returnColumn } = lookup;
  if (lookup.name === "XLOOKUP") return [matchColumn.name, returnColumn.name];
  const position = (table ?? []).findIndex(
    ({ name }) => name === returnColumn.name,
  );
  return (table ?? []).slice(0, position + 1).map(({ name }) => name);
}

/**
 * The columns a columnar Worksheet reads of each table it reads. Without
 * planned stages, or with a Formula that didn't plan, what it names isn't
 * known, so it reads every table whole.
 */
export function columnarColumnReads(
  sheet: WorkbookColumnarSheet,
  context: ColumnReadContext,
): ColumnRead[] {
  const stages = context.plan?.stages;
  if (!stages || stages.columns.length < sheet.columns.length)
    return readsWhole(sheet, context);
  return [
    { tableId: sheet.inputId, columns: inputColumns(sheet, stages.input) },
    ...sheet.lookups.map((lookup) => ({
      tableId: lookup.sourceId,
      columns: [lookup.matchColumn, lookup.valueColumn],
    })),
    ...stages.columns.flatMap(({ formula }) =>
      formulaLookups(formula).map((lookup) => ({
        tableId: lookup.tableId,
        columns: lookupColumns(lookup, context.schemas.get(lookup.tableId)),
      })),
    ),
  ];
}
